/**
 * Catálogo de variáveis de template — a lista ÚNICA do que `{{...}}` aceita.
 *
 * O editor de template mostra esta lista, a prévia substitui pelos exemplos, e
 * o motor preenche com o dado real do destinatário. Uma lista só, porque três
 * listas é como o editor passa a oferecer uma variável que o motor não conhece
 * — e "Olá {{diretor}}" chega assim, cru, na caixa de entrada da escola.
 *
 * `disponivel: 'em_breve'` marca o que depende das bases internas da EGBA
 * (sócios, contatos do administrativo/financeiro) que ainda vão ser
 * importadas: o editor já mostra — desabilitado e explicado — para o operador
 * desenhar o template de amanhã sem inventar nome de variável.
 */

export interface VariavelTemplate {
  nome: string
  rotulo: string
  exemplo: string
  grupo: 'pessoa' | 'empresa' | 'sistema'
  disponivel: 'sim' | 'em_breve'
}

export const VARIAVEIS_TEMPLATE: VariavelTemplate[] = [
  // ------------------------------------------------------------- pessoa ---
  { nome: 'primeiroNome', rotulo: 'Primeiro nome', exemplo: 'Maria', grupo: 'pessoa', disponivel: 'sim' },
  { nome: 'nome', rotulo: 'Nome completo', exemplo: 'Maria de Souza Santos', grupo: 'pessoa', disponivel: 'sim' },
  { nome: 'email', rotulo: 'E-mail', exemplo: 'diretoria@escolaexemplo.com.br', grupo: 'pessoa', disponivel: 'sim' },
  { nome: 'telefone', rotulo: 'Telefone', exemplo: '(71) 99999-0000', grupo: 'pessoa', disponivel: 'sim' },
  { nome: 'cargo', rotulo: 'Cargo', exemplo: 'Diretora administrativa', grupo: 'pessoa', disponivel: 'sim' },

  // ------------------------------------------------------------ empresa ---
  { nome: 'razaoSocial', rotulo: 'Razão social', exemplo: 'Escola Exemplo de Ensino Ltda', grupo: 'empresa', disponivel: 'sim' },
  { nome: 'cnpj', rotulo: 'CNPJ', exemplo: '12.345.678/0001-90', grupo: 'empresa', disponivel: 'sim' },
  { nome: 'municipio', rotulo: 'Município', exemplo: 'Salvador', grupo: 'empresa', disponivel: 'sim' },
  { nome: 'uf', rotulo: 'UF', exemplo: 'BA', grupo: 'empresa', disponivel: 'sim' },

  // ------------------------------------------------------------ sistema ---
  { nome: 'linkLanding', rotulo: 'Link da landing page', exemplo: 'https://exemplo.egba.ba.gov.br/lp/concluintes', grupo: 'sistema', disponivel: 'sim' },
  { nome: 'dataHoje', rotulo: 'Data de hoje', exemplo: '01/08/2026', grupo: 'sistema', disponivel: 'sim' },

  // -------------------------------------- bases internas da EGBA (futuro) ---
  { nome: 'nomeEscola', rotulo: 'Nome da escola (Censo)', exemplo: 'Colégio Exemplo', grupo: 'empresa', disponivel: 'em_breve' },
  { nome: 'nomeSocio', rotulo: 'Nome do sócio', exemplo: 'João Pereira', grupo: 'pessoa', disponivel: 'em_breve' },
]

const CONHECIDAS = new Set(VARIAVEIS_TEMPLATE.map((v) => v.nome))

/**
 * Substitui `{{variavel}}` pelos valores dados. Desconhecida vira vazio — um
 * e-mail com "Olá {{nome}}" cru é pior que um com "Olá ".
 */
export function aplicarVariaveis(texto: string, valores: Record<string, string>): string {
  return texto.replace(/\{\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*\}\}/g, (_, nome: string) => valores[nome] ?? '')
}

/** Valores de demonstração para prévia — um por variável do catálogo. */
export function valoresExemplo(): Record<string, string> {
  return Object.fromEntries(VARIAVEIS_TEMPLATE.map((v) => [v.nome, v.exemplo]))
}

/** Variáveis usadas num texto que o catálogo NÃO conhece — para avisar no editor. */
export function variaveisDesconhecidas(texto: string): string[] {
  const usadas = [...texto.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9]*)\s*\}\}/g)].map((m) => m[1]!)
  return [...new Set(usadas.filter((n) => !CONHECIDAS.has(n)))]
}
